import React, { useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { useSearchParams } from 'react-router-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  mobileFullscreen?: boolean;
  closeOnEsc?: boolean;
  closeOnClickOutside?: boolean;
  triggerAction?: string; // New prop for deep linking
  
  // TODO: Deeplinking functionality might not be needed and could clash with search param stripping elsewhere
  // (e.g., modals inside management pages). Ideally, only need one way - parent page decides if modal is open.
  // Consider removing triggerAction and URL-based modal control to simplify state management.
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  size = 'lg', // default size
  mobileFullscreen = true, // default to fullscreen on mobile
  closeOnEsc = true, // default to allowing ESC key to close
  closeOnClickOutside = true, // default to allowing click outside to close
  triggerAction, // New prop for deep linking
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if modal should be open based on URL parameter
  const urlAction = searchParams.get('action');
  const shouldOpenFromUrl = triggerAction && urlAction === triggerAction;
  const modalShouldBeOpen = isOpen || shouldOpenFromUrl;

  // Clean up URL parameter when modal closes
  const handleClose = () => {
    if (shouldOpenFromUrl) {
      // Remove the action parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('action');
      setSearchParams(newSearchParams, { replace: true });
    }
    onClose();
  };

  useLayoutEffect(() => {
    if (modalShouldBeOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [modalShouldBeOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (closeOnEsc && event.key === 'Escape') {
        handleClose();
      }
    };

    if (modalShouldBeOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [modalShouldBeOpen, closeOnEsc]);

  if (!modalShouldBeOpen) return null;

  const sizeClassesMap = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    'full': 'sm:max-w-full'
  };

  const modalContent = (
    <div className="fixed inset-0 z-1000" aria-modal="true" role="dialog">
      <div 
        className="min-h-screen px-4 text-center"
        onClick={closeOnClickOutside ? handleClose : undefined}
      >
        {/* Background overlay */}
        <div
          className={clsx(
            "fixed inset-0 transition-opacity",
            mobileFullscreen 
              ? "bg-white sm:bg-gray-500/75 sm:backdrop-blur-xs"
              : "bg-gray-500/75 backdrop-blur-xs"
          )}
          aria-hidden="true"
        />

        {/* Modal panel */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className={clsx(
            "flex min-h-full text-center",
            mobileFullscreen 
              ? "items-start justify-center p-0 sm:items-start sm:justify-center sm:p-4" 
              : "items-start justify-center p-4"
          )}>
            <div
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className={clsx(
                'overflow-y-visible relative transform text-left shadow-xl transition-all sm:my-8 sm:w-full',
                mobileFullscreen 
                  ? 'w-full min-h-screen flex flex-col bg-white overflow-y-auto sm:rounded-lg sm:bg-white sm:h-auto sm:min-h-0 sm:px-4 sm:pb-4 sm:pt-5' 
                  : 'rounded-lg bg-white px-4 pb-4 pt-5',
                sizeClassesMap[size],
                className
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Ensure document.body is available for client-side rendering
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }
  // Fallback for SSR or if document.body is not yet available, though modals are typically client-side.
  // Returning null will prevent rendering on the server or before hydration if that's an issue.
  return null; 
}; 